/**
 * AuthSplitLayout — the Figma auth screen shell.
 *
 * - With `sidePanel`: two-column split (Login / Join Waitlist) — left form
 *   column (eyebrow pill, headline, subheadline, form, footer) + right visual.
 * - Without `sidePanel`: full-width centered column (Forgot / Reset password) —
 *   optional "← Back to login" link above the headline + centered helper text.
 * Beige page background; logo pinned top-left.
 */
import React from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowLeft } from '../../design-system/icons';
import { cn } from '../../lib/utils';
import { DiscovrLogo } from '../../design-system/components/discovr-logo';

export type AuthEyebrowIcon = React.ComponentType<{ size?: number; className?: string }>;

export interface AuthSplitLayoutProps {
  eyebrow?: string;
  eyebrowIcon?: AuthEyebrowIcon;
  headline: React.ReactNode;
  subheadline?: string;
  sidePanel?: React.ReactNode;
  /** Rendered below the form (CTA link, legal links). */
  footer?: React.ReactNode;
  /** Centered helper line below the content (Forgot / Reset screens). */
  helper?: React.ReactNode;
  /** Shows a "← {backLabel}" link above the headline. */
  backTo?: string;
  /** Search params the back link keeps (e.g. a post-login `redirect`). */
  backSearch?: Record<string, string | undefined>;
  backLabel?: string;
  children: React.ReactNode;
}

export function AuthSplitLayout({
  eyebrow,
  eyebrowIcon: EyebrowIcon,
  headline,
  subheadline,
  sidePanel,
  footer,
  helper,
  backTo,
  backSearch,
  backLabel = 'Back to login',
  children,
}: AuthSplitLayoutProps) {
  const hasSide = !!sidePanel;
  return (
    <div className="flex min-h-screen bg-beige-100">
      <div className={cn('relative flex w-full flex-col', hasSide && 'lg:w-1/2')}>
        <div className="absolute top-7 left-9 z-10">
          <DiscovrLogo size={26} />
        </div>
        <div className="flex flex-1 items-center justify-center px-6 py-10">
          {/* Split (login/waitlist) = 400px column; centered (forgot/reset) =
              440px so the 48px headline fits on one line, per Figma. */}
          <div className={cn('flex w-full flex-col gap-6', hasSide ? 'max-w-[400px]' : 'max-w-[440px]')}>
            {backTo && (
              <Link
                to={backTo}
                search={backSearch}
                className="flex w-fit items-center gap-1.5 text-body-sm-medium text-foreground-subtle hover:text-foreground"
              >
                <ArrowLeft size={16} />
                {backLabel}
              </Link>
            )}
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-3">
                {eyebrow && (
                  <span className="inline-flex w-fit items-center gap-2 rounded-2xl border border-border bg-muted px-[10px] py-1.5 text-caption-medium text-(--icon-subtle) uppercase">
                    {EyebrowIcon && <EyebrowIcon size={14} className="text-foreground" />}
                    {eyebrow}
                  </span>
                )}
                <h1 className="text-heading-h2 text-foreground-ink">{headline}</h1>
              </div>
              {subheadline && <p className="text-body-md-regular text-muted-foreground">{subheadline}</p>}
            </div>
            {children}
            {footer && <div className="flex flex-col gap-5">{footer}</div>}
            {helper && <p className="text-center text-caption-regular text-foreground-subtle">{helper}</p>}
          </div>
        </div>
      </div>
      {hasSide && (
        <div className="hidden items-center justify-center px-0 py-14 lg:flex lg:w-1/2">
          {/* Figma: image is 537x796 centered in the 720x1024 right half.
              Keep that aspect, cap height at 796, and let the flex parent
              center it so the ~91px/114px margins fall out naturally. */}
          <div className="aspect-537/796 h-full max-h-[796px] overflow-hidden rounded-3xl">
            {sidePanel}
          </div>
        </div>
      )}
    </div>
  );
}
